import React, { memo } from 'react'
import PropTypes from 'prop-types'
import cn from 'comma-number'
import { Collapsible } from 'Components'
import { scrollOffset, scrollToCollapsible } from 'Utils/navigationUtils'
import NearbyMapCta from 'Components/NearbyMapCta'
import {
  _ListingDetailKeyContainer, _TitleValueBlock, _FactsContainer,
  _AnchorContainer, _Anchor, _MobileMap,
} from './styles'

const listingBuildingAgeFormatter = (buildingAge) => {
  if(!buildingAge) return '-'
  if(buildingAge === 'New') return buildingAge
  return buildingAge + ' years old'
}

const ListingDetailKeys = ({
  listing,
  isCondo,
  sizeMin,
  sizeMax,
  actualSize,
  lotSize,
  lotSizeLabel,
  sizeLabel,
  listingIsLocked,
  priceHidden,
  googleAnalyticsEvent,
  navigationLinks,
  pageModalLevel,
  collapsibleIdSuffix,
  handleDemographicMapClick,
  isMobileApp,
  isPrecon,
}) => {
  const {
    maintenance_fee: maintenanceFee, outdoor_space: outdoorSpace, building_age: buildingAge,
    exposure, date_available: possession, locker_type: locker,
    asking_price: askingPrice, closed_price: closedPrice,
    fronting_on: streetFrontage, style, tax_year: taxYear,
    offer, hydro_inc: hydroIncluded, furnished, tax, sqft, parking_type: parkingType,
  } = listing || {}
  const isRent = offer === 'Rent'
  const price = (closedPrice || askingPrice)
  const psfActual = actualSize && price && (isRent ? `$${cn((price / actualSize).toFixed(2))}` :
    `$${cn(Math.round(price / actualSize))}`)
  const psfMin = sizeMax && price && (isRent ? `$${cn((price / sizeMax).toFixed(2))}` :
    `$${cn(Math.round(price / sizeMax))}`)
  const psfMax = sizeMin && price && (isRent ? `$${cn((price / sizeMin).toFixed(2))}` :
    `$${cn(Math.round(price / sizeMin))}`)
  const sqftString = sqft ? `${sqft} sqft` : '-'

  // This needs to be computed after metric/imperial conversion to reflect the correct units
  // hide ppsqft on price is hidden since people can calculate
  const pricePerSqft = priceHidden ? '-' :
    (psfActual || (psfMin && psfMax ? (psfMin === psfMax ? psfMin : `${psfMin} - ${psfMax}`) : '-'))

  const scrollHandler = (name) => {
    scrollToCollapsible({
      section: name,
      offset: scrollOffset,
      containerId: pageModalLevel ? `appModalPage-${pageModalLevel}` : null,
    })
    googleAnalyticsEvent({
      category: 'listing',
      action: `[Key Facts Button Click][${name} Link]`,
    })
  }
  const { title, name } = listing || {}

  const occupancyDate = listing?.building?.dates?.find((date) => date?.type === 'Occupancy')?.exact_date

  return (
    <_ListingDetailKeyContainer>
      <Collapsible
        inputId={`KeyFacts-${collapsibleIdSuffix}`}
        openCollapseTargetId={`KeyFacts-${collapsibleIdSuffix}`}
        title={'Key Facts'}
        gaActionTitle={'Listing Key Facts'}
        seoText={
          `View key facts for ${title || name || ''}`
        }
        defaultShowState={!isMobileApp}
        isCollapsible={false}
        showSeparatorLine={isPrecon}
      >
        <_FactsContainer>
          {

            isPrecon ? (
              <>
                <_TitleValueBlock
                  title={`Price per ${sizeLabel}`}
                  value={pricePerSqft || '-'}
                  valueIsLocked={listingIsLocked}
                />
                <_TitleValueBlock
                  title={'Occupancy'}
                  value={occupancyDate || '-'}
                  valueIsLocked={listingIsLocked}
                />
                <_TitleValueBlock
                  title="Exposure"
                  value={exposure || '-'}
                  valueIsLocked={listingIsLocked}
                />
              </>
            ) : (
              <>
                {
                  isCondo ?
                    <_TitleValueBlock
                      title={!isRent ? 'Maintenance fees' : 'Furnished'}
                      valueIsLocked={listingIsLocked}
                      value={
                        !isRent ?
                          (maintenanceFee ? `$${cn(maintenanceFee)}` : '-') :
                          furnished
                      }
                    />
                    :
                    <_TitleValueBlock
                      title={!isRent ? 'Size' : 'Furnished'}
                      value={!isRent ? sqftString : furnished}
                      valueIsLocked={listingIsLocked}
                    />
                }
                <_TitleValueBlock
                  title={isCondo ? 'Locker' : 'Lot size'}
                  value={isCondo ? (locker || '-') : (lotSize ? `${lotSize} ${lotSizeLabel}` : '-')}
                  valueIsLocked={listingIsLocked}
                />
                <_TitleValueBlock
                  title={isCondo ? 'Exposure' : 'Street frontage'}
                  value={isCondo ? (exposure || '-') : (streetFrontage || '-')}
                  valueIsLocked={listingIsLocked}
                />
                <_TitleValueBlock
                  title={'Possession'}
                  value={possession || '-'}
                  valueIsLocked={listingIsLocked}
                />
                <_TitleValueBlock
                  title={`Price per ${sizeLabel}`}
                  value={pricePerSqft || '-'}
                  valueIsLocked={listingIsLocked}
                />

                <_TitleValueBlock
                  title={isRent ? 'Hydro included' : 'Taxes'}
                  value={isRent ? (hydroIncluded ? 'Yes' : 'No') : tax ? `$${cn(tax)} ${taxYear ? `(${taxYear})` : ''}` : '-'}
                  valueIsLocked={listingIsLocked}
                />
                {
                  isCondo ?
                    <_TitleValueBlock
                      title={'Outdoor space'}
                      value={outdoorSpace ? outdoorSpace.replaceAll(',', ', ') : '-'}
                      valueIsLocked={listingIsLocked}
                    />
                    :
                    <_TitleValueBlock
                      title={'Parking Type'}
                      value={parkingType || '-'}
                      valueIsLocked={listingIsLocked}
                    />
                }

                <_TitleValueBlock
                  title={isCondo ? 'Age of building' : 'Style'}
                  value={isCondo ? (listingBuildingAgeFormatter(buildingAge) || '-') : (style || '-')}
                  valueIsLocked={listingIsLocked}
                />
              </>
            )
          }


        </_FactsContainer>
        {!isPrecon &&
          <_AnchorContainer>
            { isCondo &&
            <_Anchor
              onClick={() => scrollHandler(navigationLinks.amenities + collapsibleIdSuffix)}
              isCondo={isCondo}
            >
              View amenities covered by maint. fees
            </_Anchor>
            }
          </_AnchorContainer>
        }
        <_MobileMap>
          <NearbyMapCta
            listing={listing}
            isMapOnlyView
            handleMapClick={handleDemographicMapClick}
            googleAnalyticsEvent={googleAnalyticsEvent}
          />
        </_MobileMap>
      </Collapsible>
    </_ListingDetailKeyContainer>
  )
}


ListingDetailKeys.propTypes = {
  listing: PropTypes.shape({
    building: PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string,
      main_photo: PropTypes.string,
      name: PropTypes.string,
      address_name: PropTypes.string,
      construction_status: PropTypes.string,
      sales_status: PropTypes.string,
      dates: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        exact_date: PropTypes.string,
      })),
    }),
  }),
  isCondo: PropTypes.bool,
  sizeMin: PropTypes.number,
  sizeMax: PropTypes.number,
  actualSize: PropTypes.number,
  lotSize: PropTypes.number,
  lotSizeLabel: PropTypes.string,
  sizeLabel: PropTypes.string,
  listingIsLocked: PropTypes.bool,
  priceHidden: PropTypes.bool,
  googleAnalyticsEvent: PropTypes.func,
  navigationLinks: PropTypes.shape({
    listingDetails: PropTypes.string,
    amenities: PropTypes.string,
  }),
  pageModalLevel: PropTypes.string,
  collapsibleIdSuffix: PropTypes.string,
  handleDemographicMapClick: PropTypes.func,
  isMobileApp: PropTypes.bool,
  isPrecon: PropTypes.bool,
}

export default memo(ListingDetailKeys)
